import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Popper from '@material-ui/core/Popper';
import Modal from '@material-ui/core/Modal';
import { AnimatePresence } from "framer-motion"
import { useDispatch, useSelector } from 'react-redux'

import { FaTimes, FaInfoCircle } from 'react-icons/fa'
import { Animation, Tooltip } from 'components'
import { LinkInput, TagInput, ContextInput, SectionSelector } from 'features'
import classes from './RichText.module.css';
// test
// import Editor from '@draft-js-plugins/editor';
import { 
    Editor, 
    EditorState, 
    RichUtils, 
    Modifier, 
    convertToRaw, 
    convertFromRaw, 
    getDefaultKeyBinding, 
    DefaultDraftBlockRenderMap, 
    SelectionState 
} from 'draft-js';
import { Map } from 'immutable'
import Toolbar from './Toolbar/Toolbar'
import ShortcutMenu from './ShortcutMenu'

import '@draft-js-plugins/inline-toolbar/lib/plugin.css';
import '@draft-js-plugins/linkify/lib/plugin.css';
import '@draft-js-plugins/anchor/lib/plugin.css'
import 'draft-js/dist/Draft.css';
import './Draft.css';
import '@draft-js-plugins/divider/lib/plugin.css';

import { 
    onPasteText, 
    onPasteFile, 
    onIndent, 
    addNewBlock, 
    renderer, 
    getCurrentBlock, 
    addEmptyBlock, 
    addNewBlockAt, 
    getSelectionRef, 
    onUnindent, 
    insertNewLine,
    insertNewBlock,
    removeLastWord,
    getCurrentWord
} from './Utils';
import { getDecorators } from './Decorators'; 
import * as actionCreators from 'store/actions'
import './Decorators/Prism/themes/prism-okadia.css'
import './Decorators/Prism/themes/prism-dark.css'
import './Decorators/Prism/themes/prism-light.css'
import { PrismDraftDecorator } from "./Decorators/Prism";

const RichTextContainer = styled.div`
    max-height: ${props => props.styles.maxHeight};
    min-height: ${props => props.styles.minHeight};
    font-size: ${props => props.styles.fontSize};
    flex-grow: 1;
    height: ${props => props.styles.height};
    display: flex;
    flex-direction: column;
    /* display: grid;
    grid-template-rows: ${props => props.showInfo & props.controls ? '2rem auto 1.5rem' 
                                : props.editable & props.controls ? '2rem auto' 
                                : 'auto'}; */
    /* grid-row-gap: 0.5rem; */
    overflow-y: hidden; // was auto previously
    overflow-x: hidden;
    margin: ${props => props.styles.margin};
    padding: ${props => props.styles.padding};
    border-width: ${props => props.styles.borderWidth};
    border-style: solid;
    border-color: ${props => props.styles.borderColor};
    border-radius: ${props => props.styles.borderRadius};
    background-color: ${props => props.styles.primaryColor};
    /* border: 1px solid yellow; */
`

const Container = styled.div`
    width: ${props => props.width ? props.width : '100%'};
    /* height: 100%; */
    overflow-y: auto;
    white-space: normal;
    overflow-wrap: break-word;
    word-wrap: break-word;
    overflow-x: hidden;
    text-overflow: ellipsis;
    display: ${props => props.maxLines ? '-webkit-box' : 'flex'};
    -webkit-line-clamp: ${props => props.maxLines ? props.maxLines : null};
    -webkit-box-orient: vertical;
    /* line-height: 1rem; */
    padding: ${props => props.editable ? '0' : '0'};
    /* padding-top: ${props => props.controls ? '0.5rem' : 0}; */
    /* display: flex; */
    flex-direction: column;
    min-height: ${props => props.minHeight};
    cursor: ${props => props.editable && 'text'};
`

const InlineButton = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: content-box;
    width: 0.8rem;
    height: 0.8rem;
    line-height: 0.8rem;
    font-size: 0.8rem;
    padding: 0.3rem;
    // border: 1px solid var(--secondary-color);
    border-radius: 5px;
    margin: 0 0.3rem 0 0;
    cursor: pointer;
    color: ${props => props.active ? 'var(--text-color)' : 'var(--secondary-accent-color)'};
    :hover{
        color: var(--text-color);
    }
`

const RichTextEditor = props => {
    const {
        primaryColor,
        secondaryColor,
        disableDropdown,
        showInfo,
        styles,
        item,
        id
    } = props
    const deco = getDecorators()
    const dispatch = useDispatch()
    const user = useSelector((state) => state.auth.user)
    const inputID = useSelector(state => state.content.inputID)
    const fileURL = useSelector((state) => state.content.url)

    const [offset, setOffset] = React.useState(0)
    // const createContent = () => {
    //     let rawState = EditorState.createWithContent(convertFromRaw(JSON.parse(props.value), deco))
    //     let newSelection = rawState.getSelection()
    //     newSelection = newSelection.merge({ focusOffset: offset, anchorOffset: offset})
    //     return EditorState.forceSelection(rawState, newSelection)
    // }
    const [editorState, setEditorState] = React.useState(props.value ? EditorState.createWithContent(convertFromRaw(JSON.parse(props.value), deco)) : EditorState.createEmpty(deco));

    const [editable, setEditable] = React.useState(props.editable ? true: false);
    const [placeholder, setPlaceholder] = React.useState(null);
    const [readOnly, setReadOnly] = React.useState(props.readOnly)
    const [focus, setFocus] = React.useState(false)
    const [focusKey, setFocusKey] = React.useState(null)
    const editor = React.useRef(null);

    const currentBlock = getCurrentBlock(editorState)
    const blockData = currentBlock.getData()
    let selectionState = editorState.getSelection()
    
    // Popper state
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [open, openPopper] = React.useState(false)
    const [popperEl, setPopperEl] = React.useState(null)
    const onPopper = (element, elBoundingRect) => {
        if(!open){
            let boundingRect
            if(elBoundingRect){
                let el = elBoundingRect
                boundingRect = {
                    getBoundingClientRect: () => ({
                        top: el.top + 8,
                        left: el.left,
                        bottom: el.bottom + 8,
                        right: el.right,
                        width: 1,
                        height: 1,
                    }),
                    clientWidth: 1,
                    clientHeight: 1
                };
            } else {
                boundingRect = getSelectionRef()
                const rect = boundingRect.getBoundingClientRect()
                boundingRect = {
                    getBoundingClientRect: () => ({
                        top: rect.top + 24,
                        left: rect.left,
                        bottom: rect.bottom + 24,
                        right: rect.right,
                        width: 1,
                        height: 1,
                    }),
                    clientWidth: 1,
                    clientHeight: 1
                };
            }
            setAnchorEl(boundingRect)
            openPopper(true)
            setPopperEl(element)
        } else {
            setAnchorEl(null)
            openPopper(false)
            setPopperEl(null)
        }
    }
    
    // Styles
    const styleMap = {
        CODE: {
            backgroundColor: secondaryColor || 'var(--code-background-color)' || 'var(--secondary-color)',
            // backgroundColor: 'white',
            color:'var(--code-color)',
            fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
            fontSize: '0.8rem',
            padding: '0rem 0.3rem',
            height: '0.8rem',
            borderRadius: '5px',
        },
        HIGHLIGHT: {
            backgroundColor: 'var(--highlight-color)',
            padding: '0 0.3rem',
            borderRadius: '5px',
            boxSizing: 'border-box',
            color: 'black'
        }
    };

    // Control focus
    let newState = editorState
    const debug = false;

    useEffect(() => {
        if(props.disableAutoFocus || !editable){ return }

        editable && editor.current.focus()
        if(props.edit && !editorState.getSelection().hasFocus){
            debug && console.log('[RichTextEditor.js] Case 1: Edit item');
            newState = EditorState.moveFocusToEnd(newState)
            setEditorState(newState)
        } else if (props.value === '' || !props.value){ // To reset after create new item
            debug && console.log('[RichTextEditor.js] Case 2: Value === ""');
            newState = EditorState.createEmpty(deco);
            setEditorState(newState);
            props.onChange && props.onChange(JSON.stringify(convertToRaw(newState.getCurrentContent())),getPlainText(newState));
            editor.current.focus()
        } 

        debug && console.log('useEffect1', { 
            has_focus: newState.getSelection().getHasFocus(), 
            endKey: newState.getSelection().getFocusKey(), 
            offset: newState.getSelection().getFocusOffset(),
            n: offset,
            editable
        })
    }, [props.value, editable, props.edit]);

    useEffect(() => {
        !props.disableAutoFocus && editable && !newState.getSelection().getHasFocus() && editor.current.focus()
        // Disabled because causes issues with focus when using ContextInput and creating new items quickly
        // setTimeout(() => {
        //     newState = EditorState.moveFocusToEnd(newState)
        //     setEditorState(newState)
        // }, 500)

        debug && console.log('useEffect2', { 
            has_focus: newState.getSelection().getHasFocus(),
            editable
        })
    }, [])

    // Update decorator
    // useEffect(() => {
    //     newState = EditorState.set(newState, {decorator: deco})
    //     setEditorState(newState)
    //     debug && console.log('useEffect2', newState.getSelection().getHasFocus(), {endKey: newState.getSelection().getFocusKey(), offset: newState.getSelection().getFocusOffset()})
    // }, [user && user['theme'], currentBlock.getType() === 'code-block' && blockData.get('syntax')]) 

    // Commit after stop typing
    useEffect(() => {

        if(props.disableAutoUpdate){
            return
        }
        const delayDebounceFn = setTimeout(() => {
            const rawData = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
            const plainText = getPlainText(editorState)
            props.onSubmit && props.onSubmit(rawData, plainText);
            props.onEdit && props.onEdit()
        }, 1000)

        // const delayDebounceFnToolbar = setTimeout(() => {
        //     !editorState.getSelection().hasFocus && setFocus(false)
        // }, 5000)
        return () => clearTimeout(delayDebounceFn)
    }, [editorState])
    
    // Plugins
    const blockRendererFn= useMemo(() => {
        const _setReadOnly = (value) => {setReadOnly(value)}
        const blockRendererFn = renderer(setEditorState, editorState, props, onPopper, _setReadOnly);
        return blockRendererFn;
    }, []);

    // Block functions
    const blockRenderMap = Map({
        'toggle': {
            element: 'figure',
            wrapper: <div style={{border: '1px solid red'}} contenteditable="false"/>,
        },
    });
    
    const extendedBlockRenderMap = DefaultDraftBlockRenderMap.merge(blockRenderMap);
    
    const blockStyleFn = (contentBlock) => {
        const type = contentBlock.getType();
        if (type === 'unordered-list-item') {
            return classes.UnorderedList;
        } else if (type === 'ordered-list-item') {
            return classes.OrderedList
        }
    }

    const toggleStyle = (event, type) => {
        let newState
        if(['BOLD','ITALIC','CODE','UNDERLINE','HIGHLIGHT'].includes(type)){
            console.log('inline')
            newState = RichUtils.toggleInlineStyle(editorState,type);
        } else if (['header-one','header-two','header-three','blockquote','unordered-list-item','ordered-list-item'].includes(type)){
            console.log('block')
            newState = RichUtils.toggleBlockType(editorState,type);
        }
        console.log(type, newState)
        newState && onInputHandler(newState);
        setPlaceholder(' ');
    }

    const toolbarToggle = (newState, type) => {
        if(type === 'IMAGE'){
            const boundingRect = getSelectionRef()
            setAnchorEl(boundingRect)
            openPopper(!open)
        } else {
            newState && onInputHandler(newState);
        }
        
        setPlaceholder(' ');
    }

    const onClose = () => {
        editor.current.focus()
        let newSelection = newState.getSelection()
        newSelection = newSelection.merge({ focusOffset: offset, anchorOffset: offset})
        newState = EditorState.forceSelection(newState, newSelection)
        setEditorState(newState)
    }

    // Utilities
    const onAddLink = (link) => {
        const selection = editorState.getSelection();
        if (!link) {
            return RichUtils.toggleLink(editorState, selection, null);
        }
        const content = editorState.getCurrentContent();
        const contentWithEntity = content.createEntity("LINK", "MUTABLE", {
            url: link
        });
        const newEditorState = EditorState.push(
            editorState,
            contentWithEntity,
            "create-entity"
        );
        const entityKey = contentWithEntity.getLastCreatedEntityKey();
        toolbarToggle(RichUtils.toggleLink(newEditorState, selection, entityKey))

        openPopper(false)
        setAnchorEl(null)
    };

    const closePopper = () => {
        setAnchorEl(null)
        openPopper(false)
        setPopperEl(null)
        dispatch(actionCreators.setInput(inputID, null))
    }

    const onPropertySelect = (value, key, sign) => {
        closePopper()
        editor.current && editor.current.focus()
        const updatedState = removeLastWord(newState, sign)
        setEditorState(updatedState)

        // Callback to ItemInput
        const rawData = JSON.stringify(convertToRaw(updatedState.getCurrentContent()));
        const plainText = getPlainText(updatedState)
        props.onProperty && props.onProperty(value,key,{raw: rawData, plain: plainText})
    }

    // Custom keyboard shortcuts
    const keyBindingFn = (event) => {
        const keyRules = props.keyRules ? props.keyRules : {};
        // Open bindings
        if(open){
            switch(event.keyCode){
                case 27: // Esc
                    closePopper()
                    break
                case 8: // Backspace
                    // closePopper()
                    break
                case 32: // Space
                    closePopper()
                    break
                case 13: // Enter
                    // closePopper()
                    return 'open-enter'
                    // return 'open-enter'
                case 40: // Down [TODO] Shift focus to popper element
                    return 'open-down'
                case 38:
                    return 'open up'
            }
        } 
        // Default bindings
        else {
            // INLINE STYLES
            if (event.keyCode === 69 && (event.ctrlKey || event.metaKey)) {
                return toggleStyle(event,'CODE')
            }  
            else if (event.keyCode === 72 && (event.ctrlKey || event.metaKey)) {
                return toggleStyle(event,'HIGHLIGHT')
            }  
            // ESC
            else if (event.keyCode === 27){
                if(keyRules['ESC']){
                    setEditable(false)
                    return keyRules['ESC']();
                } else {
                    return escHandler();
                } 
            }

            // SHIFT + ENTER
            else if (event.keyCode === 13 && event.shiftKey) {
                const newState = insertNewLine(editorState)
                setEditorState(newState)
                return
            }
            
            // ENTER
            else if (event.keyCode === 13){
                if(keyRules['ENTER']){
                    keyRules['ENTER']()
                    return
                }
                const currentBlock = getCurrentBlock(editorState)
                const blockType = currentBlock.getType()
                const blockID = currentBlock.getKey()
                if (['code-block'].includes(blockType)){
                    const newState = insertNewLine(editorState)
                    setEditorState(newState)
                    return
                }
                else if(!['unstyled','unordered-list-item','ordered-list-item'].includes(blockType)){
                    const newState = addNewBlockAt(editorState, blockID)
                    setEditorState(newState)
                    return 
                } 
            }

            // DOWN
            else if (event.keyCode === 40){
                const newState = insertNewBlock(editorState)
                if(newState){
                    setEditorState(newState)
                    return
                }
            }

            // TAB
            else if (event.keyCode === 9){
                event.preventDefault() 
                const currentBlock = getCurrentBlock(editorState)
                const blockType = currentBlock.getType()

                if(blockType !== 'code-block'){
                    if(event.shiftKey && keyRules['SHIFT_TAB']){
                        keyRules['SHIFT_TAB']()
                        editor.current.focus()
                        return
                    } else if (!event.shiftKey && keyRules['TAB']){
                        keyRules['TAB']()
                        editor.current.focus()
                        return
                    }
                }

                if(blockType === 'code-block'){
                    if(!event.shiftKey){
                        const newState = onIndent(editorState)
                        newState && setEditorState(newState)
                    } else {
                        const newState = onUnindent(editorState, 'shift-tab')
                        newState && setEditorState(newState)
                    }
                } else {
                    const newState = RichUtils.onTab(event, editorState, 4)
                    setEditorState(newState)
                }
                return
            }

            // BACKSPACE
            else if (event.keyCode === 8){
                const currentBlock = getCurrentBlock(editorState)
                const blockType = currentBlock.getType()
                if(blockType === 'code-block'){
                    event.preventDefault()
                    const newState = onUnindent(editorState, 'backspace')
                    if(newState){
                        setEditorState(newState)
                        return
                    }
                }
            }

            // CTRL + K
            else if (event.keyCode === 75 & (event.ctrlKey || event.metaKey)){
                const anchorOffset = newState.getSelection().getAnchorOffset()
                const focusOffset = newState.getSelection().getFocusOffset()
                // anchorOffset < focusOffset && onPopper(<LinkInput onSubmit={onAddLink} offsetY='1.5rem'/>)
                anchorOffset < focusOffset && onPopper('LinkInput')
            }

            // # [ContextSelector - TagInput]
            else if (event.keyCode === 51 && event.shiftKey) {
                onPopper('TagInput')
                dispatch(actionCreators.setInput(inputID, id))
            }
            
            // ! [ContextSelecotr - PriorityInput]
            else if (event.keyCode === 49 && event.shiftKey) {
                console.log('[Open PriorityInput]')
                onPopper('PriorityInput')
                dispatch(actionCreators.setInput(inputID, id))
            }

            // / [SectionSelector]
            else if (event.keyCode === 191 && props.sectionSelector){
                onPopper('SectionSelector')
                dispatch(actionCreators.setInput(inputID, id))
            }
        }
        return getDefaultKeyBinding(event);
    }

    const handleKeyCommand = (command, editorState) => {
        const newState = RichUtils.handleKeyCommand(editorState, command);        

        if (newState) {
            setEditorState(newState);
            if(shouldHidePlaceholder(newState)){
                setPlaceholder(' ');
            } else {
                setPlaceholder(null);
            }
            return 'handled';
        } 
        return 'not-handled';
    }

    const getPlainText = (newEditorState) => {
        let value = ''
        if(newEditorState.getCurrentContent()){
            const blocks = convertToRaw(newEditorState.getCurrentContent()).blocks;
            value = blocks.map(block => (!block.text.trim() && '\n') || block.text).join('\n');
            value = value.toString().trim()
        }        
        return value
    }

    const submitHandler = () => {
        setEditable(false);
        const plainText = getPlainText(editorState)
        const rawData = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
        props.onSubmit(rawData, plainText);
        props.onEdit && props.onEdit()
    }

    const escHandler = () => {
        editor.current.blur()
        setFocus(false)
        if(!props.disableClose){
            setEditable(false);
            const rawData = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
            const plainText = getPlainText(editorState)
            props.onSubmit(rawData, plainText);
            props.onEdit && props.onEdit()
        }
    }
    
    const onInputHandler = (newEditorState) => {
        // if(props.value !== '' && newEditorState.getSelection().getHasFocus()){
        if(newEditorState.getSelection().getHasFocus()){
            // console.log('[ONINPUT]', newEditorState.getSelection().serialize())
            setEditorState(newEditorState);
            setOffset(newEditorState.getSelection().getFocusOffset())
            setFocusKey(newEditorState.getSelection().getFocusKey())
            if(props.onChange){
                const plainText = getPlainText(newEditorState)
                props.onChange(JSON.stringify(convertToRaw(newEditorState.getCurrentContent())),plainText);
            }
        }
    }

    const shouldHidePlaceholder = (editorState) => {
        const contentState = editorState.getCurrentContent();
        return (
            contentState.hasText() ||
            contentState
                .getBlockMap()
                .first()
                .getType() !== 'unstyled'
        );
    }

    const onPasteTextHandler = (text, html, editorState) => {
        return onPasteText(text, html, editorState, setEditorState, onPopper, dispatch, user)
    }

    const onPasteFileHandler = (files) => {
        console.log('pasteFile',files)
        if(files.length === 1){
            onPasteFile(files[0], editorState, setEditorState, onPopper, dispatch, user)
        }
    }

    const defaultStyle = {
        primaryColor: 'var(--primary-color)',
        secondaryColor: 'var(--secondary-color)',
        margin: 0,
        padding: '0.5rem',
        borderWidth: '0px',
        borderColor: 'transparent',
        borderRadius: '5px',
        height: 'inherit',
        maxHeight: null,
        minHeight: null,
        fontSize: '1rem'
    }

    const updatedStyle = Object.assign({}, defaultStyle, styles)
    let popperElement
    switch(popperEl){
        case 'LinkInput':
            popperElement = <LinkInput onSubmit={onAddLink} onClose={closePopper} offsetY='0'/> // offsetY was 1.5rem previously
            break
        case 'TagInput':
            if(getCurrentWord(newState).includes('#')){
                popperElement = <ContextInput type='tags' title='Tags' key={id} pageID={item['pageID']}
                                context={getCurrentWord(newState,'#')} 
                                onSelect={value => {onPropertySelect(value,'tags','#')}}
                                onClose={closePopper}/>
            }
            
            break
        case 'PriorityInput':
            if(getCurrentWord(newState).includes('!')){
                popperElement = <ContextInput type='priority' title='Priority' key={id}
                                context={getCurrentWord(newState,'!')} 
                                onSelect={value => {onPropertySelect(value,'priority','!')}}
                                onClose={closePopper}/>
            }
            break
        case 'SectionSelector':
            if(getCurrentWord(newState).includes('/')){
                popperElement = <SectionSelector sectionID={item['sectionID']} pageID={item['pageID']}
                                    onSelect={(pageID, sectionID) => onPropertySelect({pageID, sectionID}, 'page-section','/')}
                                    context={getCurrentWord(newState,'/')} closeOnSelect
                                    styles={{
                                        padding: '0.1rem'
                                    }}/>
            }
    }
    return (
        <RichTextContainer styles={updatedStyle}>
            
            {/* Content */}
            <Container minHeight={updatedStyle['minHeight']} 
                scroll={props.scroll} editable={editable && !readOnly} 
                maxLines={props.maxLines} controls={props.controls & editable} 
                onClick={() => editable && !readOnly && setFocus(true)}
                >
                <Editor key={newState.getCurrentContent().getPlainText()}
                    placeholder={placeholder || props.placeholder || 'Add a note...'}
                    ref={editor}
                    editorState={newState}
                    customStyleMap={styleMap}
                    onChange={e => {onInputHandler(e)}}
                    // onBlur={() => setFocus(false)}
                    // onBlur={() => {
                    //     // open && onClose()
                    //     console.log('[BLUR]',{
                    //         newState: newState.getSelection().getFocusOffset(), 
                    //         editorState: editorState.getSelection().getFocusOffset(),
                    //         focusKey: newState.getSelection().getFocusKey(),
                    //         anchorKey: newState.getSelection().getAnchorKey(),
                    //         anchorOffset: newState.getSelection().getAnchorOffset()
                    //     })
                    // }}
                    // onFocus={() => {editable && !readOnly ? setFocus(true) : console.log('focus')}}
                    readOnly={readOnly ? true : !editable}
                    blockStyleFn={blockStyleFn}
                    keyBindingFn={keyBindingFn}
                    blockRenderMap={extendedBlockRenderMap}
                    blockRendererFn={blockRendererFn}
                    handleKeyCommand={handleKeyCommand}
                    defaultKeyBindings={false}
                    handlePastedText={onPasteTextHandler}
                    // handlePastedFiles={onPasteFileHandler}
                    />
            </Container>
            {/* {showInfo && <Button contextMenu={<ShortcutMenu/>} icon={<FaInfoCircle/>} style={{margin: '0 0 0.5rem 0', backgroundColor: updatedStyle['primaryColor'], width: '1rem', height: '1rem', padding: '0.2rem', color: 'var(--secondary-accent-color)'}}/>} */}

            {/* Toolbar */}
            <AnimatePresence>
                {editable && props.controls && focus ? 
                <Animation animation='expand'>
                    <div className={classes.ControlsContainer}>
                        <Toolbar editorState={editorState} onToggle={toolbarToggle} disableDropdown={disableDropdown} styles={{primaryColor: updatedStyle['primaryColor'], secondaryColor: updatedStyle['secondaryColor']}}/>
                        {!props.disableClose && 
                        <Tooltip title='Close' onClick={submitHandler}>
                            <InlineButton><FaTimes/></InlineButton>
                        </Tooltip>}
                    </div> 
                </Animation>: null}    
            </AnimatePresence>

            {/* Popper */}
            {/* {open && 
            <Modal onBackdropClick={() => openPopper(false)} onEscapeKeyDown={() => openPopper(false)} open={open} BackdropProps={{invisible:true}}>
                <Popper style={{zIndex: 1300, outline:'none'}} id='PopOut' open={open} anchorEl={anchorEl} placement='bottom-start'>
                    <Animation animation='default'>
                        {popperEl}
                    </Animation>
                </Popper>
            </Modal>} */}
            <AnimatePresence>
                {open ? 
                <Popper style={{zIndex: 1300, outline:'none'}} id='PopOut' open={open} anchorEl={anchorEl} placement='bottom-start'>
                    <Animation animation='default'>{popperElement}</Animation>
                </Popper> : null}
            </AnimatePresence>
            
        </RichTextContainer>
    
    );
}

export default RichTextEditor;